/*
 * <search-overlay>
 *
 * Desktop-only centered command-palette search. The persistent inline header
 * pill (input#Search-Inline) is the trigger: on desktop, focusing/clicking it
 * opens this overlay, moves focus into the overlay's own <predictive-search>
 * input, and renders results inside the centered panel.
 *
 * Reuses the global trapFocus / removeTrapFocus helpers (assets/global.js) and
 * the shared <predictive-search> component. Mobile is untouched — the trigger
 * interception only runs at >= 990px.
 */
class SearchOverlay extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('.search-overlay__panel');
    this.input = this.querySelector('input[type="search"]');
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.trigger = null;
    this.skipTriggerFocus = false;

    this.boundTriggerPointerDown = this.onTriggerPointerDown.bind(this);
    this.boundTriggerFocus = this.onTriggerFocus.bind(this);
    this.boundFinishClose = this.finishClose.bind(this);
    this.boundViewportUpdate = this.updateViewport.bind(this);

    this.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    });

    this.querySelectorAll('[data-overlay-close]').forEach((el) => {
      // Dismiss the backdrop on pointerdown (not click) so the whole overlay
      // starts closing on press. On click, the mousedown first blurs the search
      // input — hiding the predictive results — so the bare search bar stayed
      // visible until the click fired on release, a jarring two-step dismissal.
      // preventDefault stops the press from bouncing focus through <body> first.
      if (el.classList.contains('search-overlay__backdrop')) {
        el.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          this.close();
        });
      } else {
        el.addEventListener('click', () => this.close());
      }
    });

    this.setupTrigger();
    this.desktop.addEventListener('change', () => this.setupTrigger());
  }

  setupTrigger() {
    const triggerId = this.getAttribute('data-trigger');
    this.trigger = (triggerId && document.getElementById(triggerId)) || null;
    if (!this.trigger) return;

    this.trigger.removeEventListener('pointerdown', this.boundTriggerPointerDown);
    this.trigger.removeEventListener('focus', this.boundTriggerFocus);

    if (this.desktop.matches) {
      this.trigger.addEventListener('pointerdown', this.boundTriggerPointerDown);
      this.trigger.addEventListener('focus', this.boundTriggerFocus);
      // The pill is a trigger only here (its own dropdown is suppressed) — mark it
      // readonly so focus still opens the overlay but the field shows no caret and
      // can't be typed into (e.g. after close(), when focus returns to the pill).
      this.trigger.readOnly = true;
    } else {
      // Below desktop the pill is hidden and the modal search is the real input.
      this.trigger.readOnly = false;
      if (this.hasAttribute('open')) {
        // Dropped below desktop while open — close and hand back to mobile search.
        this.close();
      }
    }
  }

  onTriggerPointerDown(event) {
    if (!this.desktop.matches) return;
    // Prevent the pill from taking focus so we can redirect into the overlay.
    event.preventDefault();
    this.open(this.trigger.value);
  }

  onTriggerFocus() {
    if (!this.desktop.matches || this.skipTriggerFocus) return;
    this.open(this.trigger.value);
  }

  open(prefill) {
    if (this.hasAttribute('open') && !this.classList.contains('closing')) return;

    // Cancel any in-flight close so a quick re-open doesn't get torn down.
    clearTimeout(this.closeTimeout);
    this.panel.removeEventListener('animationend', this.boundFinishClose);
    this.classList.remove('closing');
    this.skipTriggerFocus = false;

    if (prefill && this.input) this.input.value = prefill;

    // Portal to <body> so the fixed overlay escapes the sticky header's
    // stacking context. Otherwise backdrop-filter samples the header's own
    // solid background instead of the page behind it and no blur is visible.
    if (this.parentNode !== document.body) document.body.appendChild(this);

    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');

    // Confine the overlay to the visual viewport so the on-screen keyboard (tablet)
    // can't cover the centered panel. Track keyboard/viewport changes while open.
    this.updateViewport();
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.boundViewportUpdate);
      window.visualViewport.addEventListener('scroll', this.boundViewportUpdate);
    }

    // Focus the overlay input; PredictiveSearch.onFocus fetches results if the
    // input already holds a term.
    trapFocus(this.panel, this.input);
  }

  updateViewport() {
    if (!this.hasAttribute('open') || !window.visualViewport) return;
    // Expose the visible band to CSS; the container pins itself to it so flex
    // centering keeps the panel above the keyboard. Falls back to dvh otherwise.
    this.style.setProperty('--sov-height', `${window.visualViewport.height}px`);
    this.style.setProperty('--sov-top', `${window.visualViewport.offsetTop}px`);
  }

  close() {
    if (!this.hasAttribute('open')) return;

    this.classList.add('closing');
    document.body.classList.remove('overflow-hidden');

    // Return focus to the pill without re-triggering the focus handler.
    this.skipTriggerFocus = true;
    removeTrapFocus(this.desktop.matches ? this.trigger : null);

    this.panel.addEventListener('animationend', this.boundFinishClose);
    // Fallback in case animationend never fires (e.g. reduced motion).
    this.closeTimeout = setTimeout(this.boundFinishClose, 400);
  }

  finishClose() {
    clearTimeout(this.closeTimeout);
    this.panel.removeEventListener('animationend', this.boundFinishClose);

    // Detach viewport tracking and clear the inline vars so the next open (which
    // may be on a no-keyboard desktop) starts from the CSS dvh fallback.
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.boundViewportUpdate);
      window.visualViewport.removeEventListener('scroll', this.boundViewportUpdate);
    }
    this.style.removeProperty('--sov-height');
    this.style.removeProperty('--sov-top');

    this.removeAttribute('open');
    this.classList.remove('closing');
    this.skipTriggerFocus = false;

    // Reset the query so a reopened overlay starts blank instead of showing the
    // previously typed term. On search pages the trigger pill still re-prefills via open().
    const search = this.querySelector('predictive-search');
    if (search && typeof search.closeResults === 'function') {
      search.closeResults(true);
    } else if (this.input) {
      this.input.value = '';
    }
  }
}

customElements.define('search-overlay', SearchOverlay);
